// Clone Inquiry modal: a new inquiry from an existing one. Header prefilled
// (restaurant CHANGEABLE — an inquiry doubles as a template for another
// restaurant), item checklist all ticked, one POST with the items inline. The
// BE then auto-quotes the new items from today's supplier prices and posts the
// usual new-inquiry notification; old quotes are deliberately not copied.

import {
  Box,
  Button,
  ChakraAsyncSelect,
  ChakraSelect,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import {
  PaymentOption,
  RestaurantInquiry,
  RestaurantInquiryItem,
  RestaurantOption,
  useCreateRestaurantInquiryApi,
  useSearchRestaurantsApi,
} from 'apis/restaurant-inquiries';
import { buildClonePayload } from 'pages/inquiry-processor/utils/clone-inquiry';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { isCreditOption, PAYMENT_OPTION_CHOICES } from 'pages/inquiry-processor/utils/payment';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  source: RestaurantInquiry;
  items: RestaurantInquiryItem[];
  onCreated: (id: number) => void;
}

const numOnly = (v: string) => v.replace(/[^0-9]/g, '');

const restaurantOptionOf = (inq: RestaurantInquiry): RestaurantOption => ({
  value: inq.restaurantId,
  label: inq.restaurant?.nameEn || `Restaurant #${inq.restaurantId}`,
});

export const CloneInquiryModal = ({ isOpen, onClose, source, items, onCreated }: Props) => {
  const toast = useToast();
  const { loadOptions } = useSearchRestaurantsApi();
  const createInquiry = useCreateRestaurantInquiryApi();

  const [restaurant, setRestaurant] = useState<RestaurantOption | null>(null);
  const [paymentOption, setPaymentOption] = useState<PaymentOption>(PaymentOption.CASH);
  const [creditDays, setCreditDays] = useState('');
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // `source` and `items` are fresh objects on every detail refetch, and the
  // screen refetches the selected inquiry on ANY restaurant-inquiries
  // invalidation — a dep on them would re-seed the form under the operator's
  // typing (restaurant switched back, items re-ticked). Read them through refs
  // and key the open-effect on the inquiry id, the only thing that means "a
  // different inquiry to clone". Same trap as VariantFormModal's `seed`.
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Re-seed from the source on every open — the grid can move to another
  // inquiry between opens, and a stale prefill would clone the wrong one.
  useEffect(() => {
    if (!isOpen) return;
    const src = sourceRef.current;
    setRestaurant(restaurantOptionOf(src));
    setPaymentOption(src.paymentOption);
    setCreditDays(src.creditDays ? String(src.creditDays) : '');
    setNotes(src.notes || '');
    setSelected(new Set(itemsRef.current.map((it) => it.id)));
  }, [isOpen, source.id]);

  // Legacy CREDIT is not offered for new inquiries, but a source that carries
  // it must round-trip its prefill instead of silently becoming Cash.
  // ChakraSelect (the house dropdown) takes option objects; the scalar stays
  // in state and is mapped in render.
  const paymentChoices = useMemo(() => {
    const choices = PAYMENT_OPTION_CHOICES.map((c) => ({ value: c.value, label: c.label }));
    return source.paymentOption === PaymentOption.CREDIT
      ? [...choices, { value: PaymentOption.CREDIT, label: 'Credit (legacy)' }]
      : choices;
  }, [source.paymentOption]);

  const toggle = (id: number, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const chosen = items.filter((it) => selected.has(it.id));
  const isCredit = isCreditOption(paymentOption);
  const canCreate = !!restaurant && chosen.length > 0 && (!isCredit || Number(creditDays) > 0);

  const create = async () => {
    if (!restaurant || !chosen.length) return;
    const payload = buildClonePayload(
      { restaurantId: restaurant.value, paymentOption, creditDays: Number(creditDays) || 0, notes },
      chosen
    );
    let created;
    try {
      created = await createInquiry.mutateAsync(payload);
    } catch (err) {
      toast({ title: 'Failed to clone inquiry', description: apiErrorMessage(err), status: 'error' });
      return;
    }
    toast({ title: `Inquiry #${created.id} created from #${source.id}`, status: 'success' });
    onClose();
    onCreated(created.id);
  };

  return (
    // Every exit is shut while the POST is in flight — Cancel is disabled, so
    // the overlay, Esc and the X must not be a way around it.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: '2xl' }}
      scrollBehavior='inside'
      closeOnOverlayClick={!createInquiry.isLoading}
      closeOnEsc={!createInquiry.isLoading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Clone inquiry #{source.id}</ModalHeader>
        {!createInquiry.isLoading && <ModalCloseButton />}
        <ModalBody>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='3' mb='4'>
            <FormControl isRequired gridColumn={{ md: '1 / -1' }}>
              <FormLabel fontSize='sm'>Company / Restaurant</FormLabel>
              <ChakraAsyncSelect
                loadOptions={loadOptions}
                defaultOptions
                value={restaurant}
                onChange={(opt: any) => setRestaurant(opt || null)}
                isClearable
                placeholder='Search restaurants…'
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='sm'>Payment Option</FormLabel>
              <ChakraSelect
                options={paymentChoices}
                value={paymentChoices.find((c) => c.value === paymentOption) ?? null}
                onChange={(opt: any) => opt?.value && setPaymentOption(opt.value as PaymentOption)}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition='fixed'
                styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
              />
            </FormControl>
            {isCredit && (
              <FormControl isRequired>
                <FormLabel fontSize='sm'>Credit Days</FormLabel>
                <Input
                  value={creditDays}
                  onChange={(e) => setCreditDays(numOnly(e.target.value))}
                  placeholder='e.g. 30'
                  inputMode='numeric'
                />
              </FormControl>
            )}
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel fontSize='sm'>Additional Requirements / Notes</FormLabel>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder='e.g. No Sadia brand' />
            </FormControl>
          </Grid>

          <Flex align='baseline' justify='space-between' mb='1.5'>
            <Text fontSize='sm' fontWeight='700'>
              Items to copy ({chosen.length} / {items.length})
            </Text>
            <Flex gap='3' fontSize='xs'>
              <Button variant='link' size='xs' onClick={() => setSelected(new Set(items.map((it) => it.id)))}>
                Select all
              </Button>
              <Button variant='link' size='xs' onClick={() => setSelected(new Set())}>
                None
              </Button>
            </Flex>
          </Flex>
          <Box border='1px solid' borderColor='gray.200' rounded='md' maxH='320px' overflowY='auto'>
            {items.length === 0 && (
              <Text fontSize='sm' color='gray.500' p='3'>
                This inquiry has no items to copy.
              </Text>
            )}
            {items.map((it) => {
              const mp = it.masterProduct;
              const meta = [
                it.volume != null ? `${it.volume} ${mp?.baseUnit || ''}`.trim() : null,
                it.volumePeriod ? it.volumePeriod.toLowerCase().replace('_', ' ') : null,
                mp?.brand || null,
                mp?.origin || null,
              ].filter(Boolean);
              return (
                <Flex key={it.id} px='3' py='2' borderBottom='1px solid' borderColor='gray.100' align='center' gap='3'>
                  {/* The name/meta block is the checkbox's own children: it
                      gives the box an accessible name and makes the whole row
                      text a click target. minW='0' keeps noOfLines truncating
                      inside the label span. */}
                  <Checkbox
                    w='100%'
                    sx={{ '.chakra-checkbox__label': { flex: 1, minW: 0 } }}
                    isChecked={selected.has(it.id)}
                    onChange={(e) => toggle(it.id, e.target.checked)}>
                    <Box minW='0' flex='1'>
                      <Text fontSize='sm' fontWeight='600' noOfLines={1}>
                        {mp?.name || `Item #${it.id}`}
                      </Text>
                      {meta.length > 0 && (
                        <Text fontSize='xs' color='gray.500' noOfLines={1}>
                          {meta.join(' · ')}
                        </Text>
                      )}
                    </Box>
                  </Checkbox>
                </Flex>
              );
            })}
          </Box>
          <Text fontSize='xs' color='gray.500' mt='2'>
            Quotes are not copied — the new inquiry is auto-quoted from today&apos;s supplier prices.
          </Text>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose} isDisabled={createInquiry.isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={create}
            isDisabled={!canCreate}
            isLoading={createInquiry.isLoading}
            loadingText='Creating…'>
            Create inquiry ({chosen.length} item{chosen.length === 1 ? '' : 's'})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
